import {
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
} from '@technology/web3d';
import {
  MAP_STATION_INNER_RING_OPACITY,
  MAP_STATION_OUTER_RING_OPACITY,
  MAP_STATION_MARKER_COLOR,
  MAP_STATION_ARROW_COLOR,
  MAP_STATION_ARROW_OPACITY,
  MAP_STATION_MARKER_OUTER_RING_COLOR,
  MAP_STATION_OUTER_RING_UV_DIST,
} from '../../capabilities/constants/map-station';
import { STATION_MARKER_MESH_TYPE } from '@trimble/types';

export interface StationMarkerOptions {
  planeGeometrySize: number;
  directionAngle: number;
  hasArrow: boolean;
  depthTest?: boolean;
  innerOpacity?: number;
  outerOpacity?: number;
  markerColor?: Color;
  markerOuterRingColor?: Color;
  arrowColor?: Color;
  arrowOpacity?: number;
  outerRingSize?: number;
}

/**
 * Creates a station marker mesh with custom shader for circular appearance
 * with optional directional arrow.
 *
 * @param options - Configuration options for the marker
 * @param options.directionAngle - Angle in radians for arrow direction in world space.
 *   Note: The shader applies angle negation due to UV coordinate system (Y increases downward).
 *   Callers should compute the angle accounting for this (see stations-navigation-capability
 *   for the correct calculation formula).
 * @param options.hasArrow - Whether to show directional arrow (default: false)
 * @param options.depthTest - Whether to enable depth testing (default: true)
 * @param options.outerOpacity - Opacity of the outer ring (default: 1.0)
 * @param options.innerOpacity - Opacity of the inner ring (default: 0.3)
 * @param options.markerColor - Color for the inner circle (default: white)
 * @param options.markerOuterRingColor - Color for the outer ring (default: teal)
 * @param options.arrowColor - Color for the arrow (default: {r: 0.2, g: 0.2, b: 0.2})
 * @param options.arrowOpacity - Opacity of the arrow (default: 0.6)
 * @param options.outerRingSize - Radius in UV coordinates where outer ring starts (default: 0.35, range: 0-0.5)
 * @returns A Three.js Mesh configured as a station marker
 */
export function createStationMarkerMesh(options: StationMarkerOptions): Mesh {
  const {
    planeGeometrySize,
    directionAngle,
    hasArrow,
    depthTest,
    innerOpacity = MAP_STATION_INNER_RING_OPACITY,
    outerOpacity = MAP_STATION_OUTER_RING_OPACITY,
    markerColor = MAP_STATION_MARKER_COLOR,
    markerOuterRingColor = MAP_STATION_MARKER_OUTER_RING_COLOR,
    arrowColor = MAP_STATION_ARROW_COLOR,
    arrowOpacity = MAP_STATION_ARROW_OPACITY,
    outerRingSize = MAP_STATION_OUTER_RING_UV_DIST,
  } = options;

  const geometry = new PlaneGeometry(planeGeometrySize, planeGeometrySize);

  const material = new ShaderMaterial({
    uniforms: {
      innerOpacity: { value: innerOpacity },
      outerOpacity: { value: outerOpacity },
      globalOpacity: { value: 1.0 },
      arrowAngle: { value: directionAngle },
      hasArrow: { value: hasArrow ? 1.0 : 0.0 },
      markerColor: {
        value: [markerColor.r, markerColor.g, markerColor.b],
      },
      markerOuterRingColor: {
        value: [
          markerOuterRingColor.r,
          markerOuterRingColor.g,
          markerOuterRingColor.b,
        ],
      },
      arrowColor: {
        value: [arrowColor.r, arrowColor.g, arrowColor.b],
      },
      arrowOpacity: { value: arrowOpacity },
      outerRingSize: { value: outerRingSize },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float innerOpacity;
      uniform float outerOpacity;
      uniform float globalOpacity;
      uniform float arrowAngle;
      uniform float hasArrow;
      uniform vec3 markerColor;
      uniform vec3 markerOuterRingColor;
      uniform vec3 arrowColor;
      uniform float arrowOpacity;
      uniform float outerRingSize;
      varying vec2 vUv;
      
      void main() {
        vec2 center = vec2(0.5, 0.5);
        float dist = distance(vUv, center);
        
        // Smooth circle edge with antialiasing
        float edgeSoftness = 0.01;
        float circleMask = 1.0 - smoothstep(0.5 - edgeSoftness, 0.5, dist);
        
        if (circleMask < 0.01) {
          discard;
        }
        
        // Smooth transition between inner and outer ring
        float transitionWidth = 0.02;
        float ringTransition = smoothstep(
          outerRingSize - transitionWidth, 
          outerRingSize + transitionWidth, 
          dist
        );
        
        // Mix colors and opacity based on ring position
        vec3 color = mix(markerColor, markerOuterRingColor, ringTransition);
        float alpha = mix(innerOpacity, outerOpacity, ringTransition);
        
        // Add arrow if this isn't the last station
        if (hasArrow > 0.5 && dist < 0.25) {
          vec2 centeredUV = vUv - center;
          
          // Rotate UV coordinates by arrow angle
          // Note: Negation is required due to UV coordinate system (Y increases downward)
          float cosA = cos(-arrowAngle);
          float sinA = sin(-arrowAngle);
          vec2 rotatedUV = vec2(
            centeredUV.x * cosA - centeredUV.y * sinA,
            centeredUV.x * sinA + centeredUV.y * cosA
          );
          
          // Simple arrow: two lines forming a V shape pointing right with sharp tip
          // Arrow is centered at origin (middle of arrow, not the back)
          float arrowSize = 0.20;
          float lineWidth = 0.08;
          float lineSoftness = 0.015;
          
          // Shift arrow coordinates so center of arrow is at origin (0,0)
          vec2 arrowUV = rotatedUV;
          arrowUV.x += arrowSize * 0.3; // Move arrow back so it's centered
          
          // Calculate distance to arrow lines with antialiasing
          // Upper arm: from tip (arrowSize, 0) to back (-arrowSize, arrowSize)
          float distToUpperArm = abs(arrowUV.y - (arrowSize - arrowUV.x));
          float upperArmMask = 1.0 - smoothstep(lineWidth, lineWidth + lineSoftness, distToUpperArm);
          
          // Lower arm: from tip (arrowSize, 0) to back (-arrowSize, -arrowSize)
          float distToLowerArm = abs(arrowUV.y + (arrowSize - arrowUV.x));
          float lowerArmMask = 1.0 - smoothstep(lineWidth, lineWidth + lineSoftness, distToLowerArm);
          
          // Constrain to arrow bounds
          float inBounds = step(-arrowSize, arrowUV.x) * step(arrowUV.x, arrowSize);
          float upperBounds = step(0.0, arrowUV.y) * step(arrowUV.y, arrowSize);
          float lowerBounds = step(-arrowSize, arrowUV.y) * step(arrowUV.y, 0.0);
          
          // Add sharp filled triangular point at the tip
          float tipLength = 0.08; // How far the tip extends forward
          float tipWidth = 0.08;  // Half-width of the tip base
          float tipMask = 0.0;
          
          // Filled triangle: tip at (arrowSize + tipLength, 0), base at (arrowSize, ±tipWidth)
          if (arrowUV.x > arrowSize && arrowUV.x < arrowSize + tipLength) {
            // Calculate the maximum Y at this X position (forms triangle)
            float progress = (arrowUV.x - arrowSize) / tipLength; // 0 at base, 1 at tip
            float maxY = tipWidth * (1.0 - progress); // Narrows as we go toward tip
            
            // Distance from the triangle edge with smooth antialiasing
            float distFromEdge = abs(arrowUV.y) - maxY;
            tipMask = 1.0 - smoothstep(-lineSoftness, lineSoftness, distFromEdge);
          }
          
          float arrowMask = max(
            max(
              upperArmMask * inBounds * upperBounds,
              lowerArmMask * inBounds * lowerBounds
            ),
            tipMask
          );
          
          if (arrowMask > 0.01) {
            color = mix(color, arrowColor, arrowMask);
            alpha = mix(alpha, arrowOpacity, arrowMask);
          }
        }
        
        gl_FragColor = vec4(color, alpha * circleMask * globalOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    depthTest: depthTest ?? true,
    side: DoubleSide,
  });

  const mesh = new Mesh(geometry, material);
  mesh.userData = { type: STATION_MARKER_MESH_TYPE };

  return mesh;
}
